/**
 * SettingsLayout — two-column wrapper for the settings page.
 *
 * Renders a sticky left-rail table of contents with scroll-spy alongside
 * the settings content. TOC items are grouped by the optional `group` field.
 * Section components are slotted as `children` unchanged — their behaviour
 * is not affected by this wrapper.
 *
 * The TOC rail is hidden below the `lg` breakpoint; mobile sees content-only.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface TocSection {
  /** Must match the `id` attribute on the corresponding DOM element. */
  id: string;
  /** Human-readable label shown in the TOC. */
  label: string;
  /** Optional group heading. Sections sharing a group are listed together. */
  group?: string;
}

export interface SettingsLayoutProps {
  sections: ReadonlyArray<TocSection>;
  children: ReactNode;
}

export function SettingsLayout({ sections, children }: SettingsLayoutProps) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? '');
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll-spy: observe each section element; mark whichever enters the
  // viewport as active. rootMargin shifts the trigger zone downward so the
  // active item updates just before the section reaches the top.
  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;

      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting) setActiveId(id);
        },
        { threshold: 0.25, rootMargin: '-80px 0px -60% 0px' },
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [sections]);

  function scrollToSection(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    // Also update activeId immediately so the TOC responds before the
    // IntersectionObserver fires.
    setActiveId(id);
  }

  // Build an ordered map: group label → sections (insertion order preserved).
  const groups = sections.reduce<Map<string, TocSection[]>>((acc, s) => {
    const key = s.group ?? '';
    const bucket = acc.get(key) ?? [];
    bucket.push(s);
    acc.set(key, bucket);
    return acc;
  }, new Map());

  return (
    <div className="flex gap-8" ref={containerRef}>
      {/* ── Left TOC rail (lg+ only) ──────────────────────────────────────── */}
      <nav
        aria-label="Settings navigation"
        className="hidden lg:block w-48 shrink-0"
      >
        <div className="sticky top-8 flex flex-col gap-5 py-8 pl-2">
          {Array.from(groups.entries()).map(([group, secs]) => (
            <div key={group || '__ungrouped'}>
              {group && (
                <p className="mb-1.5 text-[10px] uppercase tracking-widest text-text-3 font-medium px-2">
                  {group}
                </p>
              )}
              <ul className="flex flex-col gap-0.5">
                {secs.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      data-toc-id={s.id}
                      onClick={() => scrollToSection(s.id)}
                      className={[
                        'w-full text-left text-xs px-2 py-1 rounded-md transition-colors',
                        activeId === s.id
                          ? 'text-accent bg-accent/10 font-medium'
                          : 'text-text-2 hover:text-text-1 hover:bg-surface-glass',
                      ].join(' ')}
                    >
                      {s.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </nav>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}
